import { useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  Center,
  Container,
  Grid,
  Group,
  Loader,
  Stack,
  Text,
  Title,
  ThemeIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { CheckCircle, CreditCard, RotateCw, Star, Zap } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { paymentApi, type SatPlan } from '../../api/payment'
import { PaymentModal } from '../../components/PaymentModal'

// Simple icon per plan slot
const PLAN_ICONS = [<Zap size={28} />, <Star size={28} />, <CreditCard size={28} />]
const PLAN_COLORS = ['blue', 'teal', 'violet']

function planCodesMatch(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/** Paid period ended (or status explicitly expired). Lifetime plans have no end date. */
function isSubscriptionExpired(user: {
  subscription_status: string
  subscription_end_date?: string | null
} | null | undefined): boolean {
  if (!user) return false
  if (user.subscription_status === 'expired') return true
  if (!user.subscription_end_date) return false
  const end = new Date(user.subscription_end_date)
  return !Number.isNaN(end.getTime()) && end.getTime() < Date.now()
}

function PlanCard({
  plan,
  index,
  currentPlanCode,
  subscriptionExpired,
  onSubscribe,
}: {
  plan: SatPlan
  index: number
  currentPlanCode: string
  subscriptionExpired: boolean
  onSubscribe: (plan: SatPlan) => void
}) {
  const isThisPlan = planCodesMatch(plan.plan_code, currentPlanCode)
  const isFullySubscribed = isThisPlan && !subscriptionExpired
  const showRenew = isThisPlan && subscriptionExpired
  const color = PLAN_COLORS[index % PLAN_COLORS.length]
  const borderColor =
    isThisPlan && subscriptionExpired
      ? 'var(--mantine-color-orange-5)'
      : isThisPlan
        ? `var(--mantine-color-${color}-5)`
        : undefined

  return (
    <Card
      withBorder
      radius="lg"
      p="xl"
      style={{
        borderColor: borderColor,
        borderWidth: isThisPlan ? 2 : 1,
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isFullySubscribed && (
        <Badge
          color={color}
          variant="filled"
          radius="xl"
          style={{ position: 'absolute', top: 16, right: 16 }}
        >
          Current Plan
        </Badge>
      )}
      {showRenew && (
        <Badge
          color="orange"
          variant="filled"
          radius="xl"
          style={{ position: 'absolute', top: 16, right: 16 }}
        >
          Expired
        </Badge>
      )}

      <Stack gap="md" style={{ flex: 1 }}>
        <ThemeIcon size={52} radius="xl" color={color} variant="light">
          {PLAN_ICONS[index % PLAN_ICONS.length]}
        </ThemeIcon>

        <Box>
          <Title order={3} c="var(--sat-text)">{plan.name}</Title>
          <Text size="sm" c="var(--sat-text-muted)" mt={4}>
            {plan.description ?? 'Full access to SAT tutoring features'}
          </Text>
        </Box>

        <Group gap={4} align="baseline">
          <Text size="2rem" fw={800} c={`var(--mantine-color-${color}-6)`}>
            ${(plan.price_in_cent / 100).toFixed(2)}
          </Text>
          <Text size="sm" c="dimmed">USD</Text>
        </Group>

        <Stack gap={6} style={{ flex: 1 }}>
          {['Full practice test access', 'AI-powered study plans', 'Vocabulary mastery', 'Progress tracking'].map((feat) => (
            <Group key={feat} gap="xs">
              <CheckCircle size={15} color={`var(--mantine-color-${color}-6)`} />
              <Text size="sm">{feat}</Text>
            </Group>
          ))}
        </Stack>

        <Button
          fullWidth
          color={showRenew ? 'orange' : color}
          variant={isFullySubscribed ? 'light' : 'filled'}
          radius="xl"
          size="md"
          disabled={isFullySubscribed}
          leftSection={
            isFullySubscribed ? (
              <CheckCircle size={16} />
            ) : showRenew ? (
              <RotateCw size={16} />
            ) : (
              <CreditCard size={16} />
            )
          }
          onClick={() => onSubscribe(plan)}
          mt="sm"
        >
          {isFullySubscribed ? 'Subscribed' : showRenew ? 'Renew' : 'Subscribe'}
        </Button>
      </Stack>
    </Card>
  )
}

export function SubscriptionPage() {
  const { user, refreshUser } = useAuth()
  const subscriptionExpired = isSubscriptionExpired(user)
  const [plans, setPlans] = useState<SatPlan[]>([])
  const [platformType, setPlatformType] = useState<string>('tutoring')
  const [loading, setLoading] = useState(true)
  const [selectedPlan, setSelectedPlan] = useState<SatPlan | null>(null)

  useEffect(() => {
    Promise.all([
      paymentApi.getSatPlans(),
      paymentApi.getPlatformType(),
    ])
      .then(([plansRes, platformRes]) => {
        setPlans(plansRes.data)
        setPlatformType(platformRes.data.platform_type)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load plans', color: 'red' }))
      .finally(() => setLoading(false))
  }, [])

  async function handleSuccess() {
    await refreshUser()
    setSelectedPlan(null)
    notifications.show({
      title: 'Subscription activated',
      message: 'Your plan is now active. Enjoy your SAT prep!',
      color: 'teal',
    })
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Box ta="center">
          <Title order={1} c="var(--sat-text)">Choose Your Plan</Title>
          <Text c="var(--sat-text-muted)" mt={8} size="lg">
            {platformType === 'self_pace'
              ? 'Unlock full access to self-paced SAT prep at your own schedule'
              : 'Unlock full access to guided SAT tutoring with expert support'}
          </Text>
          {user && !subscriptionExpired && (
            <Badge color="teal" variant="light" size="lg" radius="xl" mt="sm">
              Current: {user.plan_code}
            </Badge>
          )}
          {user && subscriptionExpired && (
            <Badge color="orange" variant="light" size="lg" radius="xl" mt="sm">
              Subscription expired — renew to continue
            </Badge>
          )}
        </Box>

        {loading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : plans.length === 0 ? (
          <Text ta="center" c="dimmed" py="xl">No subscription plans available at this time.</Text>
        ) : (
          <Grid gutter="lg">
            {plans.map((plan, i) => (
              <Grid.Col key={plan.id} span={{ base: 12, sm: 6, md: 4 }}>
                <PlanCard
                  plan={plan}
                  index={i}
                  currentPlanCode={user?.plan_code ?? ''}
                  subscriptionExpired={subscriptionExpired}
                  onSubscribe={setSelectedPlan}
                />
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Stack>

      {selectedPlan && user && (
        <PaymentModal
          opened={!!selectedPlan}
          onClose={() => setSelectedPlan(null)}
          onSuccess={handleSuccess}
          planName={selectedPlan.name}
          planCode={selectedPlan.plan_code}
          amount={selectedPlan.price_in_cent}
          description={selectedPlan.description ?? `SAT Tutor — ${selectedPlan.name}`}
          userId={user.id}
        />
      )}
    </Container>
  )
}
